
import {Component, Mixins, Prop, Ref, Watch} from 'vue-property-decorator';
import {mapGetters} from 'vuex';
import ObjectInputMixin from '@/mixins/ObjectInputMixin.vue';
import {IbanTercihiEnum} from '@/plugins/uyap-plugin/uyap/IbanTercihiEnum';
import {TaskTuru} from '@/plugins/uyap-plugin/enum/TaskTuru';
import ProcessWizardTemplate, {
  WizardStep,
  DropdownOption,
  ProcessWizardVideo
} from '@/components/forms/templates/ProcessWizardTemplate.vue';
import XmlController from '@/plugins/uyap-plugin/takip_ac/XmlController';
import UyapTakipAc from '@/plugins/uyap-plugin/takip_ac/UyapTakipAc';
import {TakipAcStatusEnum} from '@/plugins/uyap-plugin/takip_ac/parser/TakipAcStatusEnum';
import StatBox from '@/components/eicrapro/shared/StatBox.vue';
import Breadcrumbs from '@/components/eicrapro/shared/Breadcrumbs.vue';
import NavigationGuard from '@/components/eicrapro/shared/NavigationGuard.vue';
import TakipHazirlaniyor from '@/components/forms/form-wizard/eicrapro/takip-hazirla/TakipHazirlaniyor.vue';
import {DosyaFormDataInterface} from '@/plugins/uyap-plugin/takip_ac/DosyaFormDataInterface';
import {ProgramPaths} from '@/enum/ProgramPaths';
import TakipBilgileriForm from "@/components/forms/form-wizard/eicrapro/takip-hazirla/TakipBilgileriForm.vue";
import {OdemeTipiEnum} from "@/plugins/uyap-plugin/takip_ac/OdemeTipleri";

export enum ActionType {
  KUYRUK_TEVZI = 'kuyrugaAktarTevziAl',
  KUYRUK_TEVZI_VE_AC = 'kuyrugaAktarTevziAlVeAc',
  HEMEN_TEVZI = 'hemenAcTevziAl',
  HEMEN_TEVZI_VE_AC = 'hemenAcTevziAlVeAc'
}

@Component({
  components: {
    NavigationGuard,
    Breadcrumbs,
    TakipBilgileriForm,
    ProcessWizardTemplate,
    StatBox,
    TakipHazirlaniyor,
  },
  computed: {
    ...mapGetters('takipHazirla', [
      'formData',
      'dosyalar',
      'dosyaValidasyonHatalari',
      'isParsing',
      'isSubmitting',
      'step2Stats'
    ]),
  },
})
export default class TakipHazirlaFormBetaWizard extends Mixins(ObjectInputMixin) {
  readonly normalTakipHazirlaVideos: ProcessWizardVideo[] = [
    {
      url: 'https://www.youtube.com/watch?v=zAx8xwJ24NI',
      description: 'Takip Hazırlama Rehberi',
      duration: '6:05'
    },
    {
      url: 'https://www.youtube.com/watch?v=k40jFKzDtdY',
      description: 'Hash Nasıl Silinir',
      duration: '0:37'
    }
  ];

  @Ref('takipBilgileriForm') readonly takipBilgileriForm!: TakipBilgileriForm;
  @Ref('wizardTemplate') readonly wizardTemplate!: ProcessWizardTemplate & {
    stepManager?: { currentStep: number; setCurrentStep: (step: number) => void }
  };
  @Ref('takipHazirlaniyor') readonly takipHazirlaniyor!: TakipHazirlaniyor;
  // Breadcrumb props
  @Prop({type: Array, default: () => ['Ana Sayfa', 'İcra Takip']})
  breadcrumbItems!: string[];

  @Prop({type: Boolean, default: false})
  showBreadcrumbHomeIcon!: boolean;

  // Enums
  OdemeTipiEnum = OdemeTipiEnum;

  // Mixin properties (from ObjectInputMixin)
  localValue!: DosyaFormDataInterface & { xml?: File | null };
  input!: () => void;

  //mapgetters type tanımlaması
  dosyalar!: UyapTakipAc[]
  dosyaValidasyonHatalari!: Array<{ tempId: number, message: string }>
  isParsing!: boolean
  isSubmitting!: boolean

  // Local state
  xmlValidationError: string | null = null;

  @Watch('localValue', {deep: true})
  onLocalValueChange(val: DosyaFormDataInterface) {
    this.$store.commit('takipHazirla/SET_FORM_DATA', val);
  }

  get secilenDosyalar(): UyapTakipAc[] {
    return this.$store.getters['takipHazirla/secilenDosyalar'] || [];
  }

  set secilenDosyalar(value: UyapTakipAc[]) {
    this.$store.commit('takipHazirla/SET_SECILEN_DOSYALAR', value);
  }

  get processingMessage(): string {
    if (this.isParsing) return 'XML dosyası işleniyor...';
    if (this.isSubmitting) return 'Takipler sıraya alınıyor...';
    return '';
  }

  get wizardSteps(): WizardStep[] {
    const baseSteps: WizardStep[] = [
      {
        id: 'step-1',
        title: 'Takip Bilgileri',
        description: 'İl, adliye ve XML dosyası seçimi',
        eyebrow: 'Takip Açılışı',
        helper: '* Zorunlu alanlar',
        canContinue: (state: DosyaFormDataInterface) => {
          // Zorunlu alanlar dolu mu?
          const missingFields = this.missingRequiredFieldsStep1;
          return missingFields.length === 0;
        },
        primaryAction: {
          label: 'Devam Et',
          handler: async (state: DosyaFormDataInterface) => {
            // 1) Form validasyonu (Vuetify rules)
            const isFormValid = this.validateStep1();

            // 2) Zorunlu alan kontrolü
            const missingFields = this.missingRequiredFieldsStep1;

            if (!isFormValid || missingFields.length > 0) {
              if (missingFields.length > 0) {
                this.$toast.error(
                    `Lütfen şu zorunlu alanları doldurun: ${missingFields.join(', ')}`
                );
              } else {
                this.$toast.error('Lütfen tüm zorunlu alanları doldurun.');
              }
              return false;
            }

            // 3) XML parsing kontrolü - zaten parse edilmiş mi?
            const vuexDosyalar = this.$store.getters['takipHazirla/dosyalar'] || [];
            const isParsing = this.$store.getters['takipHazirla/isParsing'] || false;

            // Form imzası kontrolü - form değişikliği var mı?
            const lastParsedFormDataSignature = this.$store.getters['takipHazirla/lastParsedFormDataSignature'] || '';
            const currentFormDataSignature = this.getFormDataSignature(this.localValue);
            const formDataChanged = lastParsedFormDataSignature !== currentFormDataSignature;

            // Eğer dosyalar parse edilmemişse VEYA form verileri değiştiyse VE parsing de bitmişse, şimdi parse et
            if ((vuexDosyalar.length === 0 || formDataChanged) && !isParsing) {
              // Parsing flag'ini set et
              this.$store.commit('takipHazirla/SET_IS_PARSING', true);

              // Parse işlemini async başlat (Step 2'ye geçiş parseXmlFile içinde yapılacak)
              // parseXmlFile içinde isRunQuery=true olduğunda Step 2'ye geçecek
              this.parseXmlFile().then(() => {
                this.$toast.success('XML dosyası başarıyla işlendi');
                this.$store.commit('takipHazirla/SET_IS_PARSING', false);

                // XML parse edildikten sonra son parse edilen XML'in imzasını sakla
                this.$store.commit('takipHazirla/SET_LAST_PARSED_XML_SIGNATURE', this.getXmlSignature(this.localValue.xml));

                // Form verilerinin imzasını sakla
                this.$store.commit('takipHazirla/SET_LAST_PARSED_FORM_DATA_SIGNATURE', currentFormDataSignature);
              }).catch((error) => {
                this.handleError('XML parsing failed', error);
                this.$store.commit('takipHazirla/SET_IS_PARSING', false);
                this.$store.commit('takipHazirla/SET_IS_RUN_QUERY', false);
                this.$store.commit('takipHazirla/SET_SORGULANMA_DURUMU', 0);
              });

              // İstekler atılmaya başladığında Step 2'ye geç (parseXmlFile içinde yapılacak)
              // Burada direkt true dön ki Step 2'ye geçiş yapılsın
              return true;
            }

            // Parsing devam ediyorsa bile step-2'ye geç (step-2'de loading gösterilecek)
            return true; // Adım ilerlemesine izin ver
          }
        }
      },
      {
        id: 'step-2',
        title: 'Takip Hazırlanıyor',
        description: 'Yüklenen dosyaları kontrol edin',
        eyebrow: 'Kontrol & Hazırlık',
        // Basit ve tek kaynaklı devam edilebilirlik kontrolü
        canContinue: () => this.canProceedFromStep2
      }
    ];

    // Toplam adım sayısına göre son adımda butonları ayarla (yeni obje döndürerek reaktif tut)
    const lastIndex = baseSteps.length - 1;

    const result = baseSteps.map((step, index, all) => {
      const isLast = index === lastIndex;

      if (!isLast) {
        return step;
      }

      // Son adım için özel aksiyonlar
      const stepWithActions = {
        ...step,
        primaryAction: {
          label: 'Kuyruğa Aktar',
          hasDropdown: true,
          dropdownOptions: [
            {
              label: 'Tevzi Al',
              description: 'Ödemeyi daha sonra yapacağım',
              icon: 'mdi-inbox-arrow-down',
              handler: async () => {
                await this.takipIslemiIcinOnayAl(ActionType.KUYRUK_TEVZI);
              }
            },
            {
              label: 'Tevzi Al ve Aç',
              icon: 'mdi-rocket-launch',
              handler: async () => {
                await this.takipIslemiIcinOnayAl(ActionType.KUYRUK_TEVZI_VE_AC);
              }
            }
          ] as DropdownOption[]
        },
        secondaryActions: [
          {
            label: 'Hemen Aç',
            hasDropdown: true,
            dropdownOptions: [
              {
                label: 'Tevzi Al',
                description: 'Ödemeyi daha sonra yapacağım',
                icon: 'mdi-inbox-arrow-down',
                handler: async () => {
                  await this.takipIslemiIcinOnayAl(ActionType.HEMEN_TEVZI);
                }
              },
              {
                label: 'Tevzi Al ve Aç',
                description: '....',
                icon: 'mdi-rocket-launch',
                handler: async () => {
                  await this.takipIslemiIcinOnayAl(ActionType.HEMEN_TEVZI_VE_AC);
                }
              }
            ] as DropdownOption[],
            disabled: () => {
              // Hemen Aç için sadece dosya sayısı limiti kontrolü
              // 5'ten fazla dosya seçildiğinde disabled olmalı
              return this.secilenDosyalar.length > 5;
            },
            tooltip: () => {
              // 5'ten fazla dosya seçildiğinde tooltip göster
              if (this.secilenDosyalar.length > 5) {
                return 'Hemen Aç işlemi için en fazla 5 dosya seçebilirsiniz. 5 dosyadan fazlası için "Kuyruğa Aktar" butonunu kullanınız.';
              }
              return '';
            }
          }
        ]
      };

      return stepWithActions;
    });

    return result;
  }

  get canProceedFromStep2(): boolean {
    const vuexDosyalar = this.$store.getters['takipHazirla/dosyalar'] || [];
    if (!vuexDosyalar.length) return false;
    if (!this.secilenDosyalar.length) return false;
    if (this.dosyaValidasyonHatalari.length > 0) return false;
    if (this.isSubmitting) return false;
    return true;
  }

  // Lifecycle
  async mounted() {
    this.localValue = new DosyaFormDataInterface();
    this.$store.commit('takipHazirla/RESET_FORM_STATE');
    this.input();
  }

  /**
   * Step-1 için zorunlu alanların kontrolü.
   * Eksik alan adlarını döner; hem canContinue hem de buton handler'ında kullanılır.
   */
  get missingRequiredFieldsStep1(): string[] {
    const missing: string[] = [];

    if (!this.localValue.il) missing.push('İl');
    if (!this.localValue.adliye) missing.push('Adliye');
    if (!this.localValue.kotaKullanimSekli) missing.push('Kota Tevzi Tipi');
    if (!this.localValue.xml) missing.push('XML Dosyası');

    if (
        this.localValue.ibanTercihi === IbanTercihiEnum.Avukat &&
        !this.localValue.avukatIban
    ) {
      missing.push('IBAN Seçimi');
    }

    if (this.localValue.kotaKullanimSekli === '2' && !this.localValue.tevziSiraTipi) {
      missing.push('Tevzi sıra tipi (Takip tipleri)');
    }

    return missing;
  }

  validateStep1(): boolean {
    if (!this.takipBilgileriForm) {
      return false;
    }
    return this.takipBilgileriForm.validate();
  }

  /**
   * XML dosyasının benzersiz imzasını oluşturur (name-size-lastModified)
   */
  private getXmlSignature(file: File | null): string {
    if (!file) return '';
    return `${file.name}-${file.size}-${file.lastModified}`;
  }

  /**
   * Form verilerinin benzersiz imzasını oluşturur (XML hariç)
   * Parse işleminde kullanılan form alanları değiştiğinde parse tekrar yapılması için
   */
  private getFormDataSignature(formData: DosyaFormDataInterface): string {
    // FormData'yı kopyala ve XML'i çıkar (XML değişikliği ayrı kontrol ediliyor)
    const formDataCopy = {...formData};
    delete formDataCopy.xml;

    // JSON.stringify ile serialize et ve SHA1 hash oluştur
    const Crypto = require('crypto');
    const jsonString = JSON.stringify(formDataCopy);
    return Crypto.createHash('sha1').update(jsonString).digest().toString('hex');
  }

  onXmlFileSelectedSimple(file: File | null) {
    // Son parse edilen XML imzasını store'dan al
    const lastParsedXmlSignature = this.$store.getters['takipHazirla/lastParsedXmlSignature'] || '';
    const currentXmlSignature = this.getXmlSignature(file);

    // XML değişti mi? (son parse edilen XML ile karşılaştır)
    const xmlDegisti = lastParsedXmlSignature !== currentXmlSignature;

    // NOT: XML zaten TakipBilgileriForm'da v-model ile set edildi
    // Sadece store'u güncelle
    this.$store.commit('takipHazirla/SET_FORM_DATA', {xml: file});

    // XML değiştiyse parse edilmiş dosyaları temizle
    if (xmlDegisti) {
      this.$store.commit('takipHazirla/SET_DOSYALAR', []);
      this.$store.commit('takipHazirla/SET_SECILEN_DOSYALAR', []);
      this.$store.commit('takipHazirla/SET_MAHIYETLER', []);
      this.$store.commit('takipHazirla/SET_IS_RUN_QUERY', false);
      this.$store.commit('takipHazirla/SET_SORGULANMA_DURUMU', 0);
      // Son parse edilen XML signature'ını temizle
      this.$store.commit('takipHazirla/SET_LAST_PARSED_XML_SIGNATURE', '');
      // Form imzasını da temizle (XML değiştiğinde form değişikliği kontrolü sıfırlanmalı)
      this.$store.commit('takipHazirla/SET_LAST_PARSED_FORM_DATA_SIGNATURE', '');
    }

    this.xmlValidationError = null;
    this.$store.commit('takipHazirla/SET_DOSYA_VALIDASYON_HATALARI', []);
    this.input();

    // Force update to trigger canContinue reactivity
    this.$forceUpdate();

    this.$nextTick(() => {
      if (this.takipBilgileriForm) {
        this.takipBilgileriForm.validate();
      }
    });
  }

  onXmlValidationError(message: string | null) {
    if (typeof message === 'string' && message.length) {
      this.xmlValidationError = message;
      this.$store.commit('takipHazirla/SET_DOSYA_VALIDASYON_HATALARI', [message]);
      return;
    }
    this.xmlValidationError = null;
  }

  handleCancelOperation() {
    this.$store.commit('takipHazirla/SET_IS_CANCELLED', true);
    console.log('İşlem kullanıcı tarafından iptal ediliyor...');
  }

  async parseXmlFile() {
    if (!this.localValue.xml) {
      throw new Error('XML dosyası yüklenmelidir');
    }

    try {
      // 1. XML'i oku ve parse et
      const xml = await XmlController.readXmlWithEncoded(this.localValue.xml);
      const parseResult = await XmlController.parse(xml, this.localValue);

      const sorgulanmisDosyalar: UyapTakipAc[] = [];
      const toplamDosya = parseResult.length;

      // 2. İlk state'leri set et
      this.$store.commit('takipHazirla/SET_SORGULANMA_DURUMU', 0);
      this.$store.commit('takipHazirla/SET_IS_RUN_QUERY', true);

      // 3. Step 2'ye geç (sadece Step 1'deyse)
      if (this.wizardTemplate?.stepManager?.currentStep === 1) {
        this.wizardTemplate.stepManager.setCurrentStep(2);
      }

      // UI'ın render olması için bekle
      await this.$nextTick();

      // 4. Her dosya için (sıra korunarak): UYAP sorgusu veya parse-hatalı kayıt
      for (let i = 0; i < parseResult.length; i++) {
        const item = parseResult[i];
        const takipAc = new UyapTakipAc();
        takipAc.tempId = i + 1;

        if (item.success && item.data) {
          takipAc.parsedFile = item.data;
          try {
            await takipAc.runQuery();
            if (takipAc.status !== TakipAcStatusEnum.QUERY_FAIL) {
              await takipAc.checkDuplicateTakip();
            }
          } catch (er) {
            takipAc.status = TakipAcStatusEnum.QUERY_FAIL;
            takipAc.addMessage("error", er instanceof Error ? er.message : String(er));
          }
          sorgulanmisDosyalar.push(takipAc);
        } else {
          takipAc.parsedFile = null as any;
          takipAc.status = TakipAcStatusEnum.QUERY_FAIL;
          takipAc.addMessage("error", item.message);
          sorgulanmisDosyalar.push(takipAc);
        }

        this.$store.commit('takipHazirla/SET_SORGULANMA_DURUMU', Math.round((100 / toplamDosya) * (i + 1)));
        if ((i + 1) % 5 === 0) {
          await this.$nextTick();
        }
      }

      // 5. Dosyaları Vuex'e kaydet (tek seferde - eski sistemdeki gibi)
      this.$store.commit('takipHazirla/SET_DOSYALAR', sorgulanmisDosyalar);

      // 6. Hatalı ve mükerrer uyarılı olmayanları seçili yap
      const secilenDosyalar = sorgulanmisDosyalar.filter(
          dosya => dosya.status !== TakipAcStatusEnum.QUERY_FAIL
              && dosya.status !== TakipAcStatusEnum.DUPLICATE_WARNING
      );
      this.$store.commit('takipHazirla/SET_SECILEN_DOSYALAR', secilenDosyalar);

      // 7. Mahiyet listelerini güncelle (tek commit ile - eski sistemdeki gibi)
      const mahiyetler: Array<{ mahiyetTuru: string; items: any[] }> = [];
      sorgulanmisDosyalar.forEach((dosya) => {
        if (dosya.parsedFile?.icraDosyaBilgileri?.isMahiyet?.()) {
          const mahiyetTuru = dosya.parsedFile.icraDosyaBilgileri.mahiyetTuru;
          const findMahiyet = mahiyetler.find(m => m.mahiyetTuru == mahiyetTuru);

          if (!findMahiyet && dosya.parsedFile.icraDosyaBilgileri.takipMahiyetleri) {
            mahiyetler.push({
              mahiyetTuru,
              items: dosya.parsedFile.icraDosyaBilgileri.takipMahiyetleri
            });
          }
        }
      });

      if (mahiyetler.length > 0) {
        this.$store.commit('takipHazirla/SET_MAHIYETLER', mahiyetler);
      }

      // 8. İşlem tamamlandı
      this.$store.commit('takipHazirla/SET_SORGULANMA_DURUMU', 100);
      return sorgulanmisDosyalar;
    } catch (error) {
      this.handleError('XML parsing hatası', error);
      throw error;
    } finally {
      this.$store.commit('takipHazirla/SET_IS_RUN_QUERY', false);
    }
  }

  /**
   * Merkezi error handler
   */
  private handleError(context: string, error: any, dosyaId?: number): void {
    const prefix = dosyaId ? `Dosya #${dosyaId}` : '';
    let errorMessage = error.message ? error.message : String(error);

    // 422 hatası için özel mesaj işleme
    if (error?.response?.status === 422) {
      const backendMessage = error.response?.data?.message || errorMessage;
      errorMessage = `Eksik Veri Girişi: ${backendMessage}`;
      if (error.response?.data?.hash) {
        errorMessage += ` (Hash: #${error.response.data.hash})`;
      }
    }

    console.error(`[TakipHazirlaFormBetaWizard] ${prefix} ${context}:`, error);
    this.$toast.error(`${prefix} ${context}: ${errorMessage}`);
  }

  /** OK
   * Senaryo 1: HEMEN AÇ - TEVZİ AL
   */
  async handleHemenAcTevziAl(): Promise<void> {
    try {
      const result = await this.$store.dispatch('takipHazirla/executeSenaryo1_HemenAcTevziAl');

      if (result.success) {
        this.$toast.success(result.message);
        // this.$store.commit('takipHazirla/SET_PENDING_TAKIP_HAZIRLA_RATING', true);

        // Hatalı dosyaları filtrele
        this.$store.commit('takipHazirla/SET_SECILEN_DOSYALAR',
            this.secilenDosyalar.filter(dosya => dosya.status === TakipAcStatusEnum.OPEN_FAIL)
        );
      }
    } catch (error) {
      this.handleError('handleHemenAcTevziAl hatası', error);
    }
  }

  /** OK
   * Senaryo 2: HEMEN AÇ - TEVZİ AL VE AÇ
   */
  async handleHemenAcTevziAlVeAc(): Promise<void> {
    try {
      const result = await this.$store.dispatch('takipHazirla/executeSenaryo2_HemenAcTevziAlVeAc');

      if (result.success) {
        this.$toast.success(result.message);
        // this.$store.commit('takipHazirla/SET_PENDING_TAKIP_HAZIRLA_RATING', true);

        // Hatalı dosyaları filtrele
        this.$store.commit('takipHazirla/SET_SECILEN_DOSYALAR',
            this.secilenDosyalar.filter(dosya => dosya.status === TakipAcStatusEnum.OPEN_FAIL)
        );
      }
    } catch (error) {
      this.handleError('handleHemenAcTevziAlVeAc hatası', error);
    }
  }

  /** OK
   * Senaryo 3 ve Senaryo 4 -> Kuyruğa Aktar - Genel Handler
   * @param actionType - İşlem tipi (tevzi al veya tevzi al ve aç)
   */
  async handleKuyrugaAktar(actionType: ActionType.KUYRUK_TEVZI | ActionType.KUYRUK_TEVZI_VE_AC): Promise<void> {
    try {
      // Task türünü belirle
      const taskTuru = actionType === ActionType.KUYRUK_TEVZI_VE_AC ? TaskTuru.hizli_takip_ac : TaskTuru.takip_hazirla;

      const result = await this.$store.dispatch('takipHazirla/executeSenaryo34_KuyrugaAktar', {
        actionType,
        taskTuru
      });

      // Yönlendirme (AutoPlay kontrolü)
      if (result.success) {
        // this.$store.commit('takipHazirla/SET_PENDING_TAKIP_HAZIRLA_RATING', true);

        const autoPlayEnabled = this.$store.getters['settings/autoPlayEnabled'];

        if (autoPlayEnabled && result.parentTaskId) {
          let path = "";
          if (result.taskTuru == TaskTuru.takip_hazirla)
            path = ProgramPaths.eicrapro + '/takip-hazirla-detay/' + result.parentTaskId;
          else
            path = ProgramPaths.eicrapro + '/takip-ac-detay/' + result.parentTaskId;
          this.$router.push(path);
        } else {
          // AutoPlay yoksa işlemler sayfasına git
          this.$router.push(ProgramPaths.eicrapro + '/islemler');
        }
      } else {
        this.$toast.error(result.error || 'Task oluşturulamadı. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      this.handleError('handleKuyrugaAktar hatası', error);
    }
  }

  /**
   * Event handler: Confirmation onaylandığında çağrılır
   */
  async handleConfirmationApproved(actionType: ActionType): Promise<void> {
    switch (actionType) {
      case ActionType.KUYRUK_TEVZI:
      case ActionType.KUYRUK_TEVZI_VE_AC:
        await this.handleKuyrugaAktar(actionType);
        break;
      case ActionType.HEMEN_TEVZI:
        await this.handleHemenAcTevziAl();
        break;
      case ActionType.HEMEN_TEVZI_VE_AC:
        await this.handleHemenAcTevziAlVeAc();
        break;
      default:
        console.warn('Bilinmeyen action type:', actionType);
        return;
    }
  }

  /**
   * Handler Wrapper'ları - Confirmation dialog gösterir, asıl işlem handleConfirmationApproved'de yapılır
   */

  async takipIslemiIcinOnayAl(actionType: ActionType): Promise<boolean> {
    try {
      const result = await this.$store.dispatch('takipHazirla/takipGonderimKontrolleri', {actionType})

      if (!result.success) {
        this.$toast.error(result.message, {timeout: false});
        return false;
      }

      // @ts-ignore
      this.takipHazirlaniyor.openConfirmationDialog(this.secilenDosyalar[0], actionType);

      return false;
    } catch (error) {
      this.handleError("Takip işlemi onay hatası", error);
      return false;
    }
  }
}
